import { message } from 'antd';
import React from 'react';

// ─── Brand / type tokens ──────────────────────────────────────────────────────
const TYPES = {
  success: { iconColor: '#52c41a' },
  error:   { iconColor: '#ff4d4f' },
  warning: { iconColor: '#faad14' },
  info:    { iconColor: '#1890ff' },
};

const DURATIONS = { success: 3, error: 4, warning: 4, info: 3 };

// ─── Shared content builder ───────────────────────────────────────────────────
const buildContent = (type, title, description) => {
  const t = TYPES[type] || TYPES.info;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 200 }}>
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: `${t.iconColor}1a`,
        border: `1.5px solid ${t.iconColor}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 1,
        fontWeight: 800, fontSize: 12, color: t.iconColor,
        fontFamily: 'monospace',
      }}>
        {{ success: '✓', error: '✕', warning: '!', info: 'i' }[type] || 'i'}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', lineHeight: 1.4 }}>
          {title}
        </div>
        {description && (
          <div style={{ fontSize: 12, color: '#666', marginTop: 3, lineHeight: 1.45 }}>
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

const buildStyle = () => ({
  background: 'transparent',
  border: 'none',
  boxShadow: 'none',
  padding: 0,
});

// ─── Dedup guard (prevents StrictMode double-fire and duplicate messages) ─────
const _recent = new Map();
const _DEDUP_MS = 800;

// ─── Core open() helper ───────────────────────────────────────────────────────
const open = (type, title, description, duration) => {
  try {
    const key = `${type}::${title}::${description ?? ''}`;
    const now = Date.now();
    if (_recent.has(key) && now - _recent.get(key) < _DEDUP_MS) return;
    _recent.set(key, now);
    message.destroy(); // Ensure only one message at a time
    message.open({
      icon: <span />,
      duration: duration ?? DURATIONS[type] ?? 3,
      content: buildContent(type, title, description),
      style: buildStyle(),
    });
  } catch (_) {}
};

// ─── Public API ───────────────────────────────────────────────────────────────
export const showSuccess = (title, description, extra) =>
  open('success', title, description, extra?.duration);

export const showError = (title, description, extra) =>
  open('error', title, description, extra?.duration);

export const showWarning = (title, description, extra) =>
  open('warning', title, description, extra?.duration);

export const showInfo = (title, description, extra) =>
  open('info', title, description, extra?.duration);

// ─── messageApi-compatible wrapper ───────────────────────────────────────────
// Pass this as a drop-in replacement for Ant Design's messageApi instance.
// Usage: import { notifyApi } from '../utils/notify';
//        notifyApi.success('Title', 'Description');
//        notifyApi.error('Title', 'Description');
export const notifyApi = {
  success: (title, desc, duration) => open('success', title, desc, duration),
  error:   (title, desc, duration) => open('error', title, desc, duration),
  warning: (title, desc, duration) => open('warning', title, desc, duration),
  info:    (title, desc, duration) => open('info',    title, desc, duration),
};

export default { showSuccess, showError, showWarning, showInfo, notifyApi };
